import React, { useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useLeadClock } from './LeadClockProvider';
import { LeadClockExpanded } from './LeadClockExpanded';
import { LeadClockCapsule } from './LeadClockCapsule';
/**
 * Mount inside the Hero. Holds the layout slot for the expanded card and
 * observes its own bounding rect to decide when the clock should dock.
 *
 * Stays mounted (with a min-height) even when the expanded card is hidden, so
 * the IntersectionObserver continues tracking the slot's position.
 */
export function LeadClockHeroSlot() {
  const {
    heroInView,
    setHeroInView
  } = useLeadClock();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      // Only dock when the slot has scrolled ABOVE the viewport (user has
      // passed it). If the slot is still below the viewport (e.g. on mobile
      // where it stacks below the headline at page load), keep the expanded
      // hero card. This prevents the clock from starting in the docked
      // capsule form on initial mobile load.
      const scrolledPast = entry.boundingClientRect.top < 0 && entry.intersectionRatio < 0.4;
      setHeroInView(!scrolledPast);
    }, {
      threshold: [0, 0.1, 0.3, 0.4, 0.6, 0.8, 1]
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [setHeroInView]);
  return <div ref={ref} className="w-full min-h-[420px]" data-id="element-297">
      <AnimatePresence mode="popLayout" data-id="element-298">
        {heroInView && <LeadClockExpanded key="expanded" data-id="element-299" />}
      </AnimatePresence>
    </div>;
}
/**
 * Mount inside the Navbar. Renders the capsule only when the hero card has
 * scrolled out of view. Shares a layoutId with the expanded card so
 * framer-motion handles the FLIP flight between the two slots.
 */
export function LeadClockNavSlot() {
  const {
    heroInView
  } = useLeadClock();
  return <AnimatePresence mode="popLayout" data-id="element-300">
      {!heroInView && <LeadClockCapsule key="capsule" data-id="element-301" />}
    </AnimatePresence>;
}